import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';

export interface BatchGenerateLessonsDialogData {
  seniorityLevel: string;
  specializationSlug?: string | null;
  missingTopicCount?: number;
  missingMilestoneCount?: number;
}

export interface BatchGenerateLessonsDialogResult {
  forceRegenerate: boolean;
}

@Component({
  selector: 'app-batch-generate-lessons-dialog',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatDialogModule, MatCheckboxModule, MatButtonModule],
  template: `
    <h2 mat-dialog-title>Batch generate lessons</h2>
    <form [formGroup]="form" (ngSubmit)="confirm()">
      <mat-dialog-content>
        <p class="scope">{{ scopeDescription }}</p>
        <p class="hint">
          Each lesson uses up to 5 resources. This batch should be used to fill missing lesson milestones
          for the selected scope, not just “any lesson exists”.
        </p>
        <p class="hint" *ngIf="data.missingMilestoneCount">
          Current scope gap: <strong>{{ data.missingMilestoneCount }}</strong> missing lesson milestone(s)
          across <strong>{{ data.missingTopicCount || 0 }}</strong> topic(s).
        </p>
        <p class="hint">
          Facets without an eligible unused pack are skipped. When enough unused sources exist,
          additional packs are generated for A/B comparison (served round-robin).
        </p>
        <mat-checkbox formControlName="forceRegenerate">
          Force regenerate (archive existing packs and re-pack from scratch)
        </mat-checkbox>
      </mat-dialog-content>
      <mat-dialog-actions align="end">
        <button mat-button type="button" (click)="cancel()">Cancel</button>
        <button mat-flat-button color="primary" type="submit">Queue batch</button>
      </mat-dialog-actions>
    </form>
  `,
  styles: [
    `
      .scope {
        margin: 0 0 0.75rem;
        font-weight: 500;
      }
      .hint {
        margin: 0 0 1rem;
        color: rgba(0, 0, 0, 0.6);
        font-size: 0.875rem;
      }
    `
  ]
})
export class BatchGenerateLessonsDialogComponent {
  private readonly dialogRef = inject(
    MatDialogRef<BatchGenerateLessonsDialogComponent, BatchGenerateLessonsDialogResult | undefined>
  );
  readonly data = inject<BatchGenerateLessonsDialogData>(MAT_DIALOG_DATA);
  private readonly fb = inject(FormBuilder);

  readonly form = this.fb.nonNullable.group({
    forceRegenerate: [false]
  });

  get scopeDescription(): string {
    if (this.data.specializationSlug) {
      return `${this.data.seniorityLevel} / ${this.data.specializationSlug} — focus skill topics only`;
    }
    return `${this.data.seniorityLevel} — core skill topics only`;
  }

  cancel(): void {
    this.dialogRef.close();
  }

  confirm(): void {
    this.dialogRef.close({ forceRegenerate: this.form.controls.forceRegenerate.value });
  }
}